import {useGoogleTranslate} from '../google-translate-api/contexts'
import {useQuery} from '@tanstack/react-query'
import {useMemo} from 'react'
import {useIntl} from 'react-intl'

/**
 * Custom hook to translate text using Google Translate API.
 * @param {boolean} [disabled=false] - Whether the translation is disabled.
 * @param {string} text - The text to be translated.
 * @param {string} fromLanguage - The language code of the text to be translated.
 * @return {{
 *   available: boolean,
 *   displayLang: string,
 *   displayText: string,
 *   error: Error|null,
 *   loading: boolean
 * }}
 */
const useTranslation = ({disabled, text, fromLanguage}) => {
    const {locale} = useIntl()
    const currentLang = useMemo(() => locale?.split('-')[0], [locale])
    const {translate, apiEnabled} = useGoogleTranslate()
    const available = Boolean(
        apiEnabled && text && fromLanguage && currentLang && fromLanguage !== currentLang
    )

    const {
        data,
        error: queryError,
        isInitialLoading: loading
    } = useQuery({
        queryKey: ['translation', text, fromLanguage, currentLang],
        queryFn: ({signal}) => translate(text, fromLanguage, currentLang, 'text', signal),
        enabled: available && !disabled,
        staleTime: Infinity,
        meta: {displayName: 'useTranslation'}
    })

    const error = queryError || undefined

    const translatedText = useMemo(() => data?.data?.translations?.[0]?.translatedText, [data])

    const [displayText, displayLang] = useMemo(() => {
        if (available && !disabled && !error && !loading && translatedText) {
            return [translatedText, currentLang]
        }
        return [text, fromLanguage]
    }, [available, currentLang, disabled, error, fromLanguage, loading, text, translatedText])

    return {available, displayLang, displayText, error, loading}
}

export default useTranslation
